import { Injectable, inject } from '@angular/core';
import { Observable, concat, throwError, of } from 'rxjs';
import { ISCDUser, IUser, IDeleteResponse } from '@app-root-interfaces';
import { HttpClient } from '@angular/common/http';
import { environment } from '@app-environment';
import { retry, catchError, switchMap, map, toArray } from 'rxjs/operators';
import { RootServicesModule } from './root-services.module';
import { ProfileHelper } from '../../shared/helper/profile.helper';
import { IProfile, ProfileType } from '@scf-monorepo/interfaces';

@Injectable({
    providedIn: RootServicesModule
})
export class ProfileService {
    private http = inject(HttpClient);

    public createInitialProfile(scdUser: ISCDUser): Observable<IProfile> {
        const profile: IProfile = {
            gid: scdUser.gid,
            userGid: scdUser.gid,
            title: scdUser.graduateTitle ?? '',
            type: ProfileType.ORIGINAL,
            firstName: scdUser.administrativeGivenNameInt ?? scdUser.firstName ?? '',
            surName: scdUser.surName ?? '',
            mail: scdUser.mail ?? '',
            organization: scdUser.organization ?? '',
            location: scdUser.location ?? '',
            country: scdUser.country ?? '',
            department: scdUser.department ?? '',
            displayName: scdUser.displayName ?? '',
            initials: scdUser.initials ?? '',
            phone: ProfileHelper.formatPhoneNumber(scdUser.phone) ?? '',
            mobile: ProfileHelper.formatPhoneNumber(scdUser.mobile) ?? '',
            function: scdUser.function ?? '',
            isActive: true,
            scdUser
        };
        return this.http.post<IProfile>(`${environment.gatewayAPI}/profile`, profile);
    }

    public getUserProfiles(user: IUser): Observable<IProfile[]> {
        return this.http.get<IProfile[]>(`${environment.gatewayAPI}/profile/user/${user.gid}`);
    }

    public updateProfileFromScd(
        scdUser: ISCDUser,
        profileId: string,
        userGid: string,
        isActive = true
    ): Observable<IProfile> {
        const profile: IProfile = {
            gid: scdUser.gid,
            userGid,
            title: scdUser.graduateTitle ?? '',
            type: ProfileType.ORIGINAL,
            firstName: scdUser.administrativeGivenNameInt ?? scdUser.firstName ?? '',
            surName: scdUser.surName ?? '',
            mail: scdUser.mail ?? '',
            organization: scdUser.organization ?? '',
            location: scdUser.location ?? '',
            country: scdUser.country ?? '',
            department: scdUser.department ?? '',
            displayName: scdUser.displayName ?? '',
            initials: scdUser?.initials ?? '',
            isActive,
            phone: ProfileHelper.formatPhoneNumber(scdUser.phone) ?? '',
            mobile: ProfileHelper.formatPhoneNumber(scdUser.mobile) ?? '',
            function: scdUser.function ?? '',
            updatedAt: new Date(),
            scdUser
        };
        return this.http.put<IProfile>(`${environment.gatewayAPI}/profile/${profileId}`, profile);
    }

    public copyProfileAndSetActive(copyedProfile: IProfile, selectedProfile: IProfile): Observable<IProfile[]> {
        copyedProfile = {
            ...copyedProfile,
            isActive: false,
            id: undefined,
            type: ProfileType.COPIED
        };

        selectedProfile = { ...selectedProfile };
        return this.http.put<IProfile[]>(`${environment.gatewayAPI}/profile`, [copyedProfile, selectedProfile]);
    }

    public updateProfile(profile: IProfile) {
        profile.displayName = `${profile.firstName} ${profile.surName}`;
        profile.initials = `${profile.firstName.substr(0, 1)}${profile.surName.substr(0, 1)}`;
        return this.http.put<IProfile>(`${environment.gatewayAPI}/profile/${profile.id}`, profile);
    }

    public selectProfile(profile: IProfile, _selectedProfile: IProfile) {
        return of(profile.id);
    }

    public deleteProfile(profile: IProfile) {
        return this.http.delete<IDeleteResponse<IProfile>>(`${environment.gatewayAPI}/profile/${profile.id}`);
    }

    public activateProfile(profile: IProfile, activatedProfile: IProfile) {
        profile = { ...profile, isActive: true };
        activatedProfile = { ...activatedProfile, isActive: false };
        return this.http.put<IProfile[]>(`${environment.gatewayAPI}/profile`, [profile, activatedProfile]);
    }

    // Sichere Möglichkeit finden, das Frontend und Backend in sync bleiben
    public changeSelectionAndDelete(profileToDelete: IProfile, userProfile: IProfile): Observable<IProfile> {
        const selectedProfile: IProfile = {
            ...userProfile,
            isActive: true
        };
        return concat(
            this.http.put<IProfile>(`${environment.gatewayAPI}/profile/${userProfile.id}`, selectedProfile),
            this.http.delete<IDeleteResponse<IProfile>>(`${environment.gatewayAPI}/profile/${profileToDelete.id}`).pipe(
                retry(5),
                catchError(() => {
                    return this.http
                        .put<IProfile>(`${environment.gatewayAPI}/profile/${userProfile.id}`, userProfile)
                        .pipe(switchMap(() => throwError(`Couldn't delete profile`)));
                })
            )
        ).pipe(
            toArray(),
            map(() => selectedProfile)
        );
    }

    public createSCDProfile(scdUser: ISCDUser, userId: string) {
        const profile: IProfile = {
            gid: scdUser.gid,
            userGid: userId,
            title: scdUser.graduateTitle ?? '',
            type: ProfileType.ORIGINAL,
            firstName: scdUser.administrativeGivenNameInt ?? scdUser.firstName ?? '',
            surName: scdUser.surName ?? '',
            mail: scdUser.mail ?? '',
            organization: scdUser.organization ?? '',
            location: scdUser.location ?? '',
            country: scdUser.country ?? '',
            department: scdUser.department ?? '',
            displayName: scdUser.displayName ?? '',
            initials: scdUser.initials ?? '',
            phone: ProfileHelper.formatPhoneNumber(scdUser.phone) ?? '',
            mobile: ProfileHelper.formatPhoneNumber(scdUser.mobile) ?? '',
            function: scdUser.function ?? '',
            isActive: false,
            scdUser
        };
        return this.http.post<IProfile>(`${environment.gatewayAPI}/profile`, profile);
    }
}
